import Button from 'components/Common/Button';
import Header from 'components/Layouts/components/Header';
import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';
import { Avatar, Box, styled, Typography, useTheme } from '@mui/material';
import Input from 'components/Common/Input';
import { useForm } from 'react-hook-form';
import Paper from '@mui/material/Paper';
import { useGetCurrentUser, useUpdateCurrentUser } from 'apis/user/hook';
import UserProfileSkeleton from './UserProfileSkeleton';
import TelInput from 'components/Common/TelInput';
import { GoChevronLeft } from 'react-icons/go';
import { useNavigate } from 'react-router-dom';

export default function UserProfile() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetCurrentUser();
  const { mutateAsync: updateUser, isPending } = useUpdateCurrentUser();
  const { mutateAsync: updateUserImage } = useUpdateCurrentUser(true);

  const { handleSubmit, control } = useForm({
    values: { profile: currentUser }
  });

  const [avatarUrl, setAvatarUrl] = useState<string | null>(currentUser?.avatar_url ? currentUser.avatar_url : null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const newUrl = URL.createObjectURL(file);
      setAvatarUrl(newUrl);
      const formData = new FormData();
      formData.append('profile[avatar]', file);
      await updateUserImage(formData);
    }
  };

  const onSubmit = (data: any) => {
    updateUser(data);
  };

  if (isCurrentUserLoading) {
    return <UserProfileSkeleton />;
  }

  return (
    <>
      <Header />
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          padding: { xs: 2, lg: 3 },
          paddingTop: { xs: '120px', lg: '150px' },
          width: { xs: '100%', lg: '50%' }
        }}
        marginX='auto'
      >
        <Box display='flex' alignItems='start' gap={1}>
          <Box
            onClick={() => navigate(-1)}
            display='flex'
            alignItems='center'
            justifyContent='center'
            border={`1px solid ${theme.palette.primary.light}`}
            borderRadius='50%'
            mt={0.6}
            width={22}
            height={22}
            sx={{ cursor: 'pointer', bgcolor: '#fff', flexShrink: 0 }}
          >
            <GoChevronLeft color={theme.palette.primary.main} />
          </Box>
          <Typography variant='h5' fontWeight='600' sx={{ fontSize: { xs: '1.25rem', lg: '1.5rem' } }}>
            Account Details
          </Typography>
        </Box>
        <Box component='form' onSubmit={handleSubmit(onSubmit)}>
          <Paper
            elevation={1}
            sx={{
              border: '1px solid #F3F3F3',
              padding: { xs: 2, lg: '24px' },
              borderRadius: '4px',
              marginTop: { xs: 2, lg: '24px' }
            }}
          >
            <Typography>
              Avatar
              <Typography color='red' component='span' sx={{ display: 'inline' }}>
                *
              </Typography>
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: '16px' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                marginTop: { xs: 2, sm: '8px' }
              }}
            >
              <Avatar
                src={avatarUrl || '/path/to/default/avatar.png'}
                alt={currentUser?.name ?? 'User Avatar'}
                sx={{ width: 64, height: 64, flexShrink: 0 }}
              />
              <Button
                component='label'
                role={undefined}
                variant='outlined'
                tabIndex={-1}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Choose files
                <VisuallyHiddenInput type='file' onChange={handleFileChange} accept='image/*' multiple={false} />
              </Button>
              <Typography
                variant='body2'
                color='secondary'
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, width: { xs: '100%', sm: 'auto' } }}
              >
                accepted formats: .png .jpg
              </Typography>
            </Box>
            <Grid container spacing={{ xs: 2, lg: '26px' }} marginTop={{ xs: 2, lg: '24px' }}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Input name='profile.name' label='Name' control={control} fullWidth />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Input
                  name='profile.email'
                  type='email'
                  label='Email address'
                  control={control}
                  fullWidth
                  disabled
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <TelInput
                  name='profile.phone_number'
                  label='Phone Number'
                  control={control}
                  defaultCountry='US'
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box
              sx={{
                marginTop: { xs: 2, lg: '24px' }
              }}
            >
              <Button
                type='submit'
                sx={{
                  display: 'block',
                  marginLeft: { xs: 0, lg: 'auto' },
                  width: { xs: '100%', lg: 'auto' }
                }}
                isPending={isPending}
              >
                Save Changes
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});
